import React, { KeyboardEvent } from 'react'
import {
  Box,
  TextField,
  Button,
  InputAdornment,
  IconButton,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'

interface SearchBoxProps {
  /**
   * 検索ボックスのプレースホルダーテキスト
   */
  placeholder?: string

  /**
   * 検索ボックスの現在の値
   */
  value: string

  /**
   * 検索ボックスの値が変更されたときに呼び出されるコールバック関数
   */
  onChange: (value: string) => void

  /**
   * 検索ボタンがクリックされたときに呼び出されるコールバック関数
   */
  onSearch: () => void

  /**
   * 検索ボタンのテキスト
   * @default "検索"
   */
  buttonText?: string

  /**
   * 検索ボックスを全幅で表示するかどうか
   * @default true
   */
  fullWidth?: boolean

  /**
   * 検索ボックスのバリアント
   * @default "outlined"
   */
  variant?: 'outlined' | 'standard' | 'filled'
}

/**
 * 検索機能を提供するコンポーネント
 */
export const SearchBox: React.FC<SearchBoxProps> = ({
  placeholder = 'キーワードを入力',
  value,
  onChange,
  onSearch,
  buttonText = '検索',
  fullWidth = true,
  variant = 'outlined',
}) => {
  // Enterキーが押されたときの処理
  const handleKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      onSearch()
    }
  }

  // クリアボタンがクリックされたときの処理
  const handleClear = () => {
    onChange('')
  }

  return (
    <Box
      sx={[
        {
          display: 'flex',
          alignItems: 'center',
        },
        fullWidth
          ? {
              width: '100%',
            }
          : {
              width: 'auto',
            },
      ]}
    >
      <TextField
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyPress={handleKeyPress}
        fullWidth={fullWidth}
        variant={variant}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: value ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="入力をクリア"
                onClick={handleClear}
                edge="end"
                size="small"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={[
          variant !== 'standard'
            ? {
                mr: 1,
              }
            : {
                mr: 0,
              },
        ]}
      />
      {variant !== 'standard' && (
        <Button
          variant="contained"
          color="primary"
          onClick={onSearch}
          sx={{
            minWidth: 'fit-content',
            whiteSpace: 'nowrap',
          }}
        >
          {buttonText}
        </Button>
      )}
    </Box>
  )
}

export default SearchBox
